/** Продуктовый часовой пояс проекта. */
const TZ = "Europe/Moscow";

const dayMonth = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", timeZone: TZ });
const fullDate = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: TZ });
const dateTime = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: TZ,
});

/**
 * `DailyResultItem.date` приходит как `YYYY-MM-DD` — это календарная дата
 * начисления, а не момент времени. Часовой пояс к ней применять НЕЛЬЗЯ:
 * `new Date("2026-08-21")` — полночь UTC, и в Москве она покажется тем же
 * днём, а в отрицательных зонах — предыдущим.
 */
function calendarDate(isoDate: string): Date {
  const [year = "1970", month = "01", day = "01"] = isoDate.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function formatDay(isoDate: string): string {
  return dayMonth.format(calendarDate(isoDate));
}

export function formatDate(isoDate: string): string {
  return fullDate.format(calendarDate(isoDate));
}

const slotTime = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const slotDay = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" });

/**
 * `ChartPoint.at` приходит без смещения (`2026-08-23T14:00:00`) — это стенные
 * часы продуктового пояса, а не UTC. Поэтому `Intl` здесь идёт БЕЗ `timeZone`:
 * `new Date` разберёт такую строку как локальное время, и формат вернёт ровно
 * те же цифры, что прислал бэкенд. Добавь сюда `timeZone: TZ` — и подпись
 * уедет на разницу поясов устройства и Москвы.
 *
 * `withTime` включается на почасовых периодах: на девяноста днях час в подписи
 * не несёт смысла, а места занимает вдвое больше.
 */
export function formatSlot(isoSlot: string, { withTime }: { withTime: boolean }): string {
  const moment = new Date(isoSlot);
  const day = slotDay.format(moment);
  return withTime ? `${day}, ${slotTime.format(moment)}` : day;
}

/** Для `created_at` / `purchased_at` — это уже настоящие моменты времени с TZ. */
export function formatDateTime(isoDateTime: string): string {
  return dateTime.format(new Date(isoDateTime));
}

/**
 * День настоящего момента времени — для `launched_at` и подобных.
 *
 * Отдельно от `formatDate`: та принимает КАЛЕНДАРНУЮ дату `YYYY-MM-DD` и
 * разбирает её по дефисам, поэтому на строке с временем даёт Invalid Date.
 * Здесь на входе `timestamptz`, и часовой пояс применять НАДО — иначе робот,
 * запущенный в три часа ночи по Москве, показал бы предыдущий день.
 */
export function formatMomentDay(isoDateTime: string): string {
  return fullDate.format(new Date(isoDateTime));
}

/**
 * Разложить оставшиеся секунды на дни и часы для подписи «До окончания: 2д 14ч».
 *
 * Секунды приходят с бэкенда, посчитанные по СЕРВЕРНЫМ часам. Вычитать
 * `ends_at` из `Date.now()` нельзя: §10 доработок требует, чтобы срок
 * предложения не зависел от локального времени клиента, а QA-чеклист §13 прямо
 * велит проверить ручную смену времени устройства. Устройство, переведённое на
 * два дня вперёд, показало бы «завершено» на живой кампании.
 *
 * Целочисленное деление, а не `toFixed`: гард качества запрещает
 * `parseFloat`/`parseInt`/`toFixed` вне `money.ts`, и правильно запрещает —
 * округлять здесь нечего.
 */
export function splitCountdown(seconds: number): {
  days: number;
  hours: number;
  minutes: number;
} {
  const safe = Math.max(seconds, 0);
  return {
    days: Math.floor(safe / 86_400),
    hours: Math.floor((safe % 86_400) / 3_600),
    minutes: Math.floor((safe % 3_600) / 60),
  };
}
